<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  summary: {
    type: Object,
    default: () => ({}),
  },
  activeStatus: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectStatus']);

const { t } = useI18n();

const EXCLUSIVE_KEYS = [
  'pending',
  'sent',
  'delivered',
  'read',
  'replied',
  'failed',
];

const FUNNEL_STATUSES = {
  pending: ['pending'],
  sent: ['sent', 'delivered', 'read', 'replied'],
  delivered: ['delivered', 'read', 'replied'],
  read: ['read', 'replied'],
  replied: ['replied'],
  failed: ['failed'],
};

const TILE_META = [
  { key: 'total', color: 'text-n-slate-12' },
  { key: 'pending', color: 'text-n-slate-12' },
  { key: 'sent', color: 'text-n-blue-11' },
  { key: 'delivered', color: 'text-n-teal-11' },
  { key: 'read', color: 'text-n-teal-11' },
  { key: 'replied', color: 'text-n-teal-11' },
  { key: 'failed', color: 'text-n-ruby-11' },
];

const exclusiveCount = key => props.summary?.[key] || 0;

const totalCount = computed(() =>
  EXCLUSIVE_KEYS.reduce((sum, key) => sum + exclusiveCount(key), 0)
);

const funnelCount = key => {
  if (key === 'total') return totalCount.value;
  return FUNNEL_STATUSES[key].reduce(
    (sum, status) => sum + exclusiveCount(status),
    0
  );
};

const formatPercent = count => {
  if (!totalCount.value) return '0%';
  return `${((count / totalCount.value) * 100).toFixed(1)}%`;
};

const tiles = computed(() =>
  TILE_META.map(meta => {
    const value = funnelCount(meta.key);
    const filterKey = meta.key === 'total' ? '' : meta.key;

    return {
      ...meta,
      filterKey,
      label: t(`CAMPAIGN.ANALYTICS.STATUS.${meta.key.toUpperCase()}`),
      help: t(`CAMPAIGN.ANALYTICS.HELP.${meta.key.toUpperCase()}`),
      value,
      percent: formatPercent(value),
      isActive:
        meta.key === 'total'
          ? !props.activeStatus
          : props.activeStatus === meta.key,
    };
  })
);
</script>

<template>
  <div class="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
    <button
      v-for="tile in tiles"
      :key="tile.key"
      type="button"
      class="flex flex-col items-start gap-1 p-4 text-left transition-colors rounded-xl outline outline-1 outline-n-container bg-n-solid-2 hover:bg-n-solid-3"
      :class="{ 'ring-2 ring-n-brand': tile.isActive }"
      @click="emit('selectStatus', tile.filterKey)"
    >
      <span class="flex items-center gap-1 text-sm font-medium text-n-slate-11">
        {{ tile.label }}
        <fluent-icon
          v-tooltip="tile.help"
          icon="info"
          size="14"
          class="text-n-slate-10"
        />
      </span>
      <span class="text-2xl font-medium" :class="tile.color">
        {{ tile.value }}
      </span>
      <span class="text-sm text-n-slate-11">{{ tile.percent }}</span>
    </button>
  </div>
</template>
